/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";

// CSS Import
import "./index.css";

// REDUX Imports
import { resetGetPanError } from "../../../redux/features/verifyPAN/verifyPAN";

// UTLIS Imports
import { GlobalConfiguration } from "../../../utils/configurationProvider";
import Validation from "../../../utils/validation";
import formatGSTDate from "../../../utils/formatGSTDate";

// COMPONENTS Imports
import Loader from "../../../components/Loader/Index";
import CommonDetailCard from "../../../components/_utils/CommonDetailCard";
import useServiceChecker from "../../../components/ServiceChecker";
import TitleSubtitlePair from "../../../components/_utils/TitleSubtitlePair";
import CommonToast from "../../../components/_utils/CommonToast";
import SwitcherTabs from "../../../components/_utils/CommonButtons/SwitcherTabs";
import TopNavSmallButton from "../../../components/_utils/CommonButtons/TopNavSmallButton";
import PrimaryButton from "../../../components/_utils/CommonButtons/PrimaryButton";
import SearchBar from "../../../components/_utils/CommonSearchComponents/SearchBar";
import LargePopupRedirect from "../../../components/_utils/CommonPopups/CommonLargePopupRedirect";
import GSTVerification from "../GSTDetails";
import TopNavComponent from "../../../components/_utils/CommonTopNav/TopNavComponent";
import ICONS from "../../../assets/Icons";
import useRestrictedAction from "../../../hooks/useRestrictedAction";
import {
  getPANDetails,
  verifyPAN,
  resetSelectedPan,
  resetPANDetails,
} from "../../../redux/features/sharedResources/sharedResources";
import globalText from "../../../utils/globalText";

const PanVerification = ({ pan_no, popup }) => {
  const inputRef = useRef("");
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { state } = useLocation();

  // useState
  const [panNo, setPanNo] = useState("");
  const [prevPanNo, setPrevPanNo] = useState("");
  const [updatedPan, setUpdatePan] = useState("");
  const [panStatus, setPanStatus] = useState(null);
  const [showGstPopup, setGstShowPopup] = useState(false);
  const [gstNo, setGstNo] = useState("");
  const services = useSelector((state) => state.refreshToken.services);
  const [ensureAny] = useServiceChecker();

  const updatedPanno = location.state
    ? location.state.panNumber
    : panNo
      ? panNo
      : "";
  const { status } = ensureAny(["VGSTIN"]);
  const loader = ICONS?.LOADER_GIF;

  const handlePan = (value) => {
    if (value.length > GlobalConfiguration.panLength) {
      return;
    }
    if (!specialRegex.test(value)) {
      return;
    }
    setPanNo(value.toUpperCase());
    setPanStatus(null);
    setPrevPanNo("");
  };

  const {
    panDetailsLoading,
    panDetailsError,
    panDetailsData,
    selectedPanData,
    panVerificationLoading,
  } = useSelector((state) => state.sharedResources);

  const panRegex = Validation.panRegex;
  const specialRegex = Validation.alphanumericRegex;

  const handleArbitraryPan = async (panNo) => {
    if (!panNo || !panNo.length) {
      dispatch(resetPANDetails());
      return;
    }

    if (
      panNo.length === GlobalConfiguration.panLength &&
      !panRegex.test(panNo)
    ) {
      dispatch(resetGetPanError());
      return;
    }

    if (panNo.length === GlobalConfiguration.panLength) {
      dispatch(resetPANDetails());

      const response = await dispatch(getPANDetails({ search_string: panNo }));
      if (response?.payload?.gstin_details === undefined) {
        await dispatch(verifyPAN({ search_string: panNo }));
      }
    }
  };

  const handlePanClick = async (pan_no = "") => {
    const finalPanNo = popup ? pan_no : panNo;

    if (!finalPanNo || !finalPanNo.length) {
      return;
    }

    if (
      finalPanNo.length === GlobalConfiguration.panLength &&
      !panRegex.test(finalPanNo)
    ) {
      setPanStatus("error");
      dispatch(resetGetPanError());

      CommonToast({
        text: "Invalid format for PAN ID.",
        icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
        variant: "var(--error)",
      });
      return;
    }

    if (finalPanNo?.length === GlobalConfiguration?.panLength) {
      setPanStatus("success");
      setUpdatePan(finalPanNo);
      setPrevPanNo(finalPanNo);
      dispatch(resetPANDetails());

      await dispatch(getPANDetails({ search_string: finalPanNo }));
    }
  };

  const handleKeyDown = (event) => {
    if (popup) return;
    if (event.key === "Enter" && !isPanDisabled) {
      handlePanClick();
    }
  };

  const handleNavigate = (gst) => {
    if (ensureAny(["VGSTIN"]).status) {
      setGstShowPopup(true);
      setGstNo(gst);
    } else {
      navigate("/feature_not_assigned", {
        state: {
          unavailableService: "VGSTIN",
        },
      });
    }
  };

  const shouldSearchBarBeHidden =
    popup ||
    (state !== null && typeof state === "string") ||
    (selectedPanData !== null && location.state) ||
    location.state?.panNumber;

  const handleUpdateDetails = async () => {
    const up = updatedPan ?? state;

    CommonToast({
      text: "Updating details.",
      icon: loader,
      variant: "var(--processing)",
      buttonNeeded: false,
      btnProps: {
        isDisabled: true,
        enabledTextColor: "var(--text-primary)",
        enabledBgColor: "var(--primary-bg)",
      },
    });

    const response = await dispatch(verifyPAN({ search_string: up }));

    if (response?.payload?.status === true) {
      toast.dismiss();
      CommonToast({
        text: "Details updated successfully.",
        icon: ICONS?.SELECT_ALL_WITH_WHITE_FILLED_CIRCLE,
        variant: "var(--success)",
        btnProps: {
          isDisabled: false,
          enabledTextColor: "var(--text-primary)",
          enabledBgColor: "var(--primary-bg)",
        },
      });
    } else {
      toast.dismiss();
      CommonToast({
        text: "Failed to update.",
        icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
        variant: "var(--error)",
        btnProps: {
          isDisabled: false,
          enabledTextColor: "var(--text-primary)",
          enabledBgColor: "var(--primary-bg)",
        },
      });
    }
  };

  const isPanLengthValid = panNo.length === GlobalConfiguration.panLength;
  const isPanChanged = panNo && panNo !== prevPanNo;
  const hasPanLoading = !!panDetailsLoading;
  const hasPanError = !!panDetailsError && !isPanChanged;

  const isPanDisabled =
    !isPanLengthValid ||
    hasPanLoading ||
    hasPanError ||
    panStatus === "error" ||
    (!isPanChanged && panStatus === "success") ||
    (panDetailsData?.status &&
      panDetailsData?.pan_details?.pan_number === panNo);

  const { showRestrictedAction, RestrictedActionPopup } = useRestrictedAction();

  const actionRestricted = () => {
    showRestrictedAction({
      path: "/settings/PAN_verification",
      state: {
        panNumber: panNo,
      },
    });
  };
  useEffect(() => {
    if (services && !services?.includes("VPAN")) {
      navigate("/feature_not_assigned");
    }

    if (!location.state && !popup) {
      dispatch(resetPANDetails());
      setPanNo("");
      dispatch(resetSelectedPan());
      return;
    }

    if (updatedPanno) {
      setPanNo(updatedPanno);
      setUpdatePan(updatedPanno);
      handleArbitraryPan(updatedPanno);
    } else if (state !== null && typeof state === "string") {
      setPanNo(state);
      setUpdatePan(state);
      handleArbitraryPan(state);
    } else if (selectedPanData && location.state) {
      setPanNo(selectedPanData);
      setUpdatePan(selectedPanData);
    }
    return () => {
      dispatch(resetSelectedPan());
    };
  }, []);

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [panNo, popup, isPanDisabled]);

  useEffect(() => {
    if (popup) {
      if (
        pan_no?.length === GlobalConfiguration.panLength &&
        panRegex.test(pan_no)
      ) {
        setPanNo(pan_no);
        handlePanClick(pan_no);
      } else {
        dispatch(resetPANDetails());
      }
    }
  }, [popup, pan_no]);

  useEffect(() => {
    if (
      !panRegex?.test(pan_no) &&
      pan_no?.length === GlobalConfiguration.panLength
    ) {
      handlePanClick(pan_no);
    }
  }, [popup]);

  return (
    <>
      <div
        className={`flex flex-col Qmd:mt-margin-base bg-primary-bg ${popup ? "h-full overflow-auto" : "h-screen"}`}
      >
        <div
          className={`bg-primary-nav shadow-box-shadow-bottom fixed top-top-base left-left-base z-z-index-tooltip w-width-full block ${popup ? "hidden" : "Qsmd:hidden"}`}
        >
          <TopNavComponent
            isFormattingNotRequired={true}
            topNavHeader="PAN Verification"
          />
        </div>

        <>{RestrictedActionPopup}</>
        {showGstPopup && (
          <LargePopupRedirect
            headerTitle="GST Details"
            isfullViewrequired={true}
            fullviewhandleFunction={() =>
              navigate(`/settings/GST_verification`, {
                state: gstNo,
              })
            }
            closeiconhandleFunction={() => setGstShowPopup(false)}
          >
            <GSTVerification
              gst_number={gstNo}
              popup={true}
            />
          </LargePopupRedirect>
        )}
        <div
          style={{ display: popup && "none" }}
          className="hidden Qsmd:flex min-h-navbar-breadcrumb-height bg-primary-nav"
        ></div>
        <div
          className={`w-width-full bg-primary-nav opacity-base-opacity flex justify-center items-end z-z-index-header p-mobile-padding-l Qmd:p-padding-md mt-margin-xl Qsmd:mt-margin-base shadow-box-shadow-bottom ${
            popup ? "Qmd:px-padding-md Qmd:pb-padding-button-l hidden " : ""
          }`}
        >
          <div className="flex flex-col h-[fit-content] justify-center items-center max-w-tab-width-normal Qmd:flex-row w-width-full Qmd:w-width-full Qmd:py-padding-base">
            <SearchBar
              searchText={panNo}
              searchRef={inputRef}
              placeholder="Enter PAN number"
              isSearchDisabled={false}
              onInput={handlePan}
              btnText="Fetch"
              onClick={() => {
                if (isPanDisabled) return;
                if (
                  (isPanLengthValid && panDetailsLoading === null) ||
                  isPanChanged
                ) {
                  handlePanClick();
                }
              }}
              isBtnDisable={isPanDisabled}
              txtColor={
                !isPanDisabled ? "var(--primary-bg)" : "var(--text-primary)"
              }
              btnColor={
                !isPanDisabled ? "var(--text-primary)" : "var(--text-disabled)"
              }
              inActiveVariantNeeded={true}
            />
          </div>
        </div>
        {panDetailsLoading ? (
          <>
            <div
              className={`gst-loading-loader h-height-lg flex justify-center items-center ${
                shouldSearchBarBeHidden ? "" : ""
              }`}
            >
              <Loader />
            </div>
          </>
        ) : !panDetailsLoading && panDetailsData?.status ? (
          <>
            <div
              className={`hidden Qmd:flex cin_details_pan  ${
                popup ? "px-padding-base" : ""
              }
             `}
              style={{
                marginTop: shouldSearchBarBeHidden ? "" : "",
              }}
            >
              <div className="flex items-center justify-center w-width-full max-w-tab-width-normal h-fit">
                <div className="flex w-width-full justify-between h-[var(--topnav-small-btn-width)]">
                  <div className="flex flex-row w-width-full h-height-full">
                    <div className="flex flex-col w-width-md py-padding-md Qmd:pl-padding-md Qlg:pl-padding-base pr-padding-md gap-y-gap-xs">
                      <TitleSubtitlePair
                        titleColor="text-primary"
                        variant="md"
                        title={panDetailsData?.pan_details?.full_name
                          .toLowerCase()
                          .split(" ")
                          .map(
                            (word) =>
                              word.charAt(0).toUpperCase() + word.slice(1),
                          )
                          .join(" ")}
                        subtitle="Legal name"
                      />
                    </div>
                    <div className="my-margin-md border-l border-solid border-primary-bg"></div>
                    <div className="flex flex-col w-width-md py-padding-md ml-margin-md gap-y-gap-xs">
                      <TitleSubtitlePair
                        title={panDetailsData?.pan_details?.pan_number}
                        subtitle={globalText?.PAN_NUMBER}
                        titleColor="text-primary"
                        variant="md"
                      />
                    </div>
                  </div>
                  <div
                    className={`flex  ${
                      popup
                        ? "opacity-light-opacity cursor-not-allowed"
                        : "cursor-pointer"
                    }`}
                  >
                    {panDetailsData?.status ? (
                      <>
                        <TopNavSmallButton
                          btnText={
                            <>
                              Refresh <br /> details
                            </>
                          }
                          btnType="button"
                          handleFunction={() => {
                            if (popup) {
                              actionRestricted();
                              return;
                            }
                            handleUpdateDetails();
                          }}
                          isDisabled={popup || panVerificationLoading}
                          enabledTextColor="var(--text-primary)"
                          enabledBgColor="var(--primary-nav-act)"
                          disabledBgColor="var(--primary-nav-act)"
                          icon={ICONS?.REFRESH_BALANCE}
                          hoverOption="white"
                        />
                      </>
                    ) : (
                      <></>
                    )}
                  </div>
                </div>
              </div>
            </div>
            {!popup && (
              <div className="px-padding-md bg-primary-nav block Qsmd:hidden"></div>
            )}
            <div className={`Qmd:hidden flex flex-col bg-primary-nav `}>
              <div className="flex flex-col p-mobile-padding-l Qmd:p-padding-md gap-gap-sm">
                <TitleSubtitlePair
                  title={panDetailsData?.pan_details?.full_name
                    .toLowerCase()
                    .split(" ")
                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(" ")}
                  subtitle="Legal name"
                  titleColor="text-primary"
                  variant="md"
                />
              </div>
              <div className="mx-mobile-margin-l Qmd:mx-margin-md border-primary-bg border-t-border"></div>
              <div className="flex flex-col gap-gap-sm p-mobile-padding-l Qmd:p-padding-md">
                <TitleSubtitlePair
                  title={panDetailsData?.pan_details?.pan_number}
                  subtitle={globalText?.PAN_NUMBER}
                  titleColor="text-primary"
                  variant="md"
                />
              </div>
            </div>
            <div className="Qmd:hidden">
              {/* <div className="spacing w-width-full py-padding-sm"></div> */}
              <div className="pt-padding-md">
                <div className="flex flex-row py-padding-sm px-mobile-padding-l bg-primary-card items-center">
                  <span className="text-font-sm font-semibold text-text-primary">
                    Quick Actions
                  </span>
                </div>
                {panDetailsData?.status ? (
                  <>
                    <TopNavSmallButton
                      btnText="Refresh Details."
                      btnType="button"
                      handleFunction={() => {
                        if (popup) {
                          actionRestricted();
                          return;
                        }
                        handleUpdateDetails();
                      }}
                      isDisabled={popup || panVerificationLoading}
                      enabledTextColor="var(--text-primary)"
                      enabledBgColor="var(--primary-nav-act)"
                      disabledBgColor="var(--primary-nav-act)"
                      icon={ICONS?.REFRESH_BALANCE}
                      hoverOption="white"
                      fullWidthRequired={true}
                    />
                  </>
                ) : (
                  <></>
                )}
              </div>
            </div>
            <div
              className={`flex items-center justify-center w-width-full bg-primary-bg pt-padding-md px-mobile-padding-l Qmd:px-padding-md ${popup ? "Qlg:px-padding-base" : ""}`}
            >
              <div className="max-w-tab-width-normal w-max-content-width h-full">
                <div className="flex w-[fit-content] gap-gap-sm bg-primary-card rounded-border-radius-md Qsm:overflow-x-hidden">
                  <SwitcherTabs
                    tabs={[
                      {
                        label: "Associated GSTs",
                        key: "associated_gsts",
                        handleFunction: () => {},
                      },
                    ]}
                  />
                </div>

                {panDetailsData?.gstin_details ? (
                  <div className="py-padding-md">
                    <div
                      className={`pan_gst_have Qmd:justify-start items-center flex-col Qmd:flex-row Qmd:items-start ${!popup ? "cursor-pointer" : ""}`}
                    >
                      {panDetailsData?.gstin_details?.map((val) => {
                        const entries = [
                          {
                            heading: globalText?.PAN_NUMBER,
                            content: val?.pan_no,
                            type: "text",
                            isDisabled: popup,
                          },
                          {
                            heading: globalText?.GSTIN,
                            content: val?.gstin_no,
                            type: "text",
                            isDisabled: popup,
                          },
                          {
                            heading: "Company name",
                            content: val?.business_name,
                            type: "text",
                            isDisabled: popup,
                          },
                          {
                            heading: "Estd. on",
                            content: formatGSTDate(val?.date_of_registration),
                            type: "text",
                            isDisabled: popup,
                          },
                          {
                            heading: "Company status",
                            content: val?.status,
                            type: "text",
                            isDisabled: popup,
                          },
                        ];

                        return (
                          <CommonDetailCard
                            cardTitle="GST Details"
                            numCols={1}
                            onClick={() => {
                              if (popup) {
                                actionRestricted();
                                return;
                              }
                              handleNavigate(val?.gstin_no);
                            }}
                            variant="fourth"
                            entries={entries}
                            isDisabled={!status || popup}
                          />
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <div className="w-width-full Qsmd:w-fit bg-primary-card mt-margin-md rounded-border-radius-md">
                    <p className="p-padding-md text-text-primary text-font-md font-sans">
                      No associated GSTs found.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </>
        ) : !panDetailsLoading && panDetailsError === true ? (
          <div className="flex flex-col justify-center items-center mx-auto my-auto">
            <img
              src={ICONS?.NO_DATA_FOUND}
              alt="notfound"
              className="px-padding-md w-graphic-width-md h-graphic-rectangle-height-md"
            />
            <p className="text-font-sm text-text-primary text-center font-sans font-normal py-padding-sm">
              No data found.
            </p>
            <PrimaryButton
              btnText="Clear parameters"
              enabledBgColor="var(--primary-act)"
              enabledTextColor="var(--text-primary)"
              handleFunction={() => {
                dispatch(resetPANDetails());
                setPanNo("");
              }}
              hoverOption="white"
              isDisabled={false}
            />
          </div>
        ) : (
          <div className="flex flex-col justify-center items-center mx-auto my-auto">
            <img
              src={ICONS?.IFSC_VERIFICATION_GRAPHICS}
              alt="bgDefault"
              className="px-padding-md h-graphic-square-height-md w-graphic-width-md"
            />
            <p className="text-font-md text-text-primary font-normal text-center py-padding-sm">
              Enter PAN number in the search bar,
              <br /> to fetch the verified PAN details.
            </p>
          </div>
        )}
      </div>
    </>
  );
};

export default PanVerification;
